import { pluralize } from "@sellar-io/common";
import { AlertCompact } from "../../../components/alert/AlertCompact";
import { useSupplierRelationshipDiscountList } from "../../../hooks/api/v2/supplierRelationshipDiscount/useSupplierRelationshipDiscountList";
import { useSupplierRelationshipProductItemDiscountList } from "../../../hooks/api/v2/supplierRelationshipProductItemDiscount/useSupplierRelationshipProductItemDiscountList";
import { Button, Icon } from "@sellar-io/components";
import { useState } from "react";
import { SlideTransition } from "../../../components/transition/SlideTransition";
import { BusinessConnectionConvertDiscountsDialog } from "./BusinessConnectionConvertDiscountsDialog";
import {
  generateConnectionDiscountDescription,
  generateConnectionItemDiscountDescription,
} from "./BusinessConnectionConvertDiscountsCard.shared";

export const BusinessConnectionConvertDiscountsCard = ({
  connectionId,
}: {
  connectionId: number;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [convertActive, setConvertActive] = useState(false);

  const connectionItemDiscountListQuery =
    useSupplierRelationshipProductItemDiscountList({
      query: [
        {
          type: "SupplierRetailerConnectionId",
          modifier: "EQUALS",
          value: connectionId,
        },
      ],
    });

  const connectionItemDiscounts =
    connectionItemDiscountListQuery.data?.pages.flat() ?? [];

  const connectionDiscountListQuery = useSupplierRelationshipDiscountList({
    query: [
      {
        type: "SupplierRetailerConnectionId",
        modifier: "EQUALS",
        value: connectionId,
      },
    ],
  });

  const connectionDiscounts =
    connectionDiscountListQuery.data?.pages.flat() ?? [];

  const numberOfDiscounts =
    connectionItemDiscounts.length + connectionDiscounts.length;

  const discounts = (
    <>
      {connectionDiscounts.map((discount) => {
        const text = generateConnectionDiscountDescription(discount);
        return <DiscountItem key={discount.id} text={text} />;
      })}
      {connectionItemDiscounts.map((discount) => {
        const text = generateConnectionItemDiscountDescription(discount);
        return <DiscountItem key={discount.id} text={text} />;
      })}
    </>
  );

  return (
    <>
      {numberOfDiscounts > 0 && (
        <div className="rounded-lg border border-zinc-300">
          <div className="rounded-t-lg border-b border-zinc-300 bg-zinc-100 px-3 py-2">
            Customer specific discounts set up
          </div>
          <div className="flex flex-col gap-2 px-3 py-2">
            <p>
              Convert this customer's discounts into a derived price list for
              more flexible pricing tooling. You can also consolidate several
              customers' discount set ups at once.
            </p>
            <AlertCompact
              type="information"
              className="w-full text-sm"
              onClick={() => setExpanded(!expanded)}
            >
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center justify-between">
                  <span className="font-bold">
                    {numberOfDiscounts}{" "}
                    {pluralize("discount", numberOfDiscounts)} set
                  </span>
                  <span className="flex items-center gap-2 font-bold text-indigo-800 md:ml-2">
                    {expanded ? "Hide discounts" : "Show discounts"}
                    <Icon icon={expanded ? "CHEVRON_UP" : "CHEVRON_DOWN"} />
                  </span>
                </div>
                <SlideTransition
                  show={expanded}
                  className="flex flex-col gap-1"
                >
                  {discounts}
                </SlideTransition>
              </div>
            </AlertCompact>
            <Button onClick={() => setConvertActive(true)}>
              Convert into price list
            </Button>
          </div>
        </div>
      )}
      <BusinessConnectionConvertDiscountsDialog
        active={convertActive}
        setActive={setConvertActive}
        discounts={discounts}
        connectionId={connectionId}
      />
    </>
  );
};

export const DiscountItem = ({ text }: { text: string }) => {
  return (
    <div className="flex items-center gap-2">
      <Icon icon="CIRCLE" size={0.3} />
      {text}
    </div>
  );
};
